import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronDown } from 'lucide-react';

export const FAQ = () => {
  const faqs = [
    {
      category: 'Orders & Shipping',
      questions: [
        {
          q: 'How long does delivery take?',
          a: 'Standard delivery takes 3-5 business days. We offer free shipping on orders over €50.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Currently, we ship within the United States. International shipping will be available soon.'
        },
        {
          q: 'Can I track my order?',
          a: 'Yes! Once your order ships, you\'ll receive a tracking number via email.'
        }
      ]
    },
    {
      category: 'Payment',
      questions: [
        {
          q: 'What payment methods do you accept?',
          a: 'We currently accept Cash on Delivery (COD) for all orders. Additional payment methods will be added soon.'
        },
        {
          q: 'Is Cash on Delivery safe?',
          a: 'Yes, COD is completely safe. You pay only when you receive your products.'
        }
      ]
    },
    {
      category: 'Products',
      questions: [
        {
          q: 'Are your products natural?',
          a: 'Yes, all our products are made with natural ingredients and are dermatologically tested.'
        },
        {
          q: 'How do I know which product is right for me?',
          a: 'Each product page includes detailed information about ingredients, usage, and skin types. You can also contact our support team for personalized recommendations.'
        },
        {
          q: 'Do you test on animals?',
          a: 'No, all our products are cruelty-free and never tested on animals.'
        }
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We accept returns within 30 days of delivery for unopened products in original packaging.'
        },
        {
          q: 'How do I request a return?',
          a: 'Contact our customer service team with your order number, and we\'ll guide you through the return process.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen py-16">
      <div className="container-custom max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-5xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-muted-foreground">
            Find answers to common questions about our products and services
          </p>
        </motion.div>

        <div className="space-y-8">
          {faqs.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: catIndex * 0.1 }}
            >
              <h2 className="font-serif text-2xl font-semibold mb-4">{category.category}</h2>
              <div className="space-y-4">
                {category.questions.map((faq, qIndex) => (
                  <Card key={qIndex}>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center justify-between">
                        {faq.q}
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center p-8 bg-muted/30 rounded-2xl"
        >
          <h3 className="font-serif text-2xl font-semibold mb-3">Still have questions?</h3>
          <p className="text-muted-foreground mb-6">
            Our customer service team is here to help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:support@naturalcare.com" className="text-primary hover:underline">
              support@naturalcare.com
            </a>
            <span className="hidden sm:inline text-muted-foreground">|</span>
            <a href="tel:+15551234567" className="text-primary hover:underline">
              +1 (555) 123-4567
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};